/**
 * V2-702.5: right-pane content of the Analytics page. Mirrors the V1
 * dashboard's per-window detail: header, minute-by-minute strip + table,
 * action bar (re-fetch / edit / hide — Phase 3 wired to backend), Market,
 * Bet, Probability Tracking, 1s price feed (M1-M5 tabs), Condition
 * Checks.
 *
 * All sections render conditionally — `awaiting_resolution` and `no_bet`
 * windows hide what doesn't apply, matching V1 behaviour.
 */
import { Fragment, useEffect, useMemo, useState } from "react";

import {
  EditOrderModal, HideWindowConfirm, RefetchOutcomeModal,
} from "./AnalyticsActionModals";
import { momentumFromState, resolveDecisionConfig, sideOdds, synthesizeDecisionChecks } from "./decisionChecksFallback";
import {
  Anchor, Badge, Button, Card, Code, Group, SegmentedControl, Stack, Table, Text, Title, Divider,
  ScrollArea, Box, NumberInput,
} from "@mantine/core";
import { IconAnchor, IconCheck, IconExternalLink, IconRefresh, IconEdit, IconEye, IconEyeOff, IconX } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";

import { api } from "../api/client";
import type {
  AnalyticsWindowDetail, MinuteAggregate, ProbabilityTiming, WindowTick, DecisionCheckRow,
} from "../api/schemas";
import { Decimal, ErrorState, LoadingState } from "../components/ui";
import { useMarket } from "../hooks/useMarket";
import { polymarketEventUrl } from "../lib/polymarketUrl";
import {
  LEAF_SPAN_SECONDS,
  pickTiersForWindow,
} from "./pricefeedHierarchy";
import { monoStyle } from "../theme";

interface Props {
  slug: string;
}

function checkSecondOffset(
  checkTs: string,
  windowStart: string,
  // V3 multi-market: window length varies (5m..4h). Defaults to 300s
  // so callers that haven't been threaded yet keep V2 5m behaviour.
  windowSeconds: number = 300,
): number | null {
  const checkMs = Date.parse(checkTs);
  const windowStartMs = Date.parse(windowStart);
  if (!Number.isFinite(checkMs) || !Number.isFinite(windowStartMs)) return null;
  const sec = Math.floor((checkMs - windowStartMs) / 1000);
  if (!Number.isFinite(sec)) return null;
  return Math.max(0, Math.min(windowSeconds - 1, sec));
}

function checkTimeLabel(checkTs: string, windowStart: string, windowSeconds: number = 300): string {
  const sec = checkSecondOffset(checkTs, windowStart, windowSeconds);
  const windowStartMs = Date.parse(windowStart);
  if (sec == null || !Number.isFinite(windowStartMs)) {
    return new Date(checkTs).toLocaleTimeString();
  }
  return new Date(windowStartMs + (sec * 1000)).toLocaleTimeString();
}

function collapseChecksBySecond(
  checks: DecisionCheckRow[],
  windowStart: string,
  windowSeconds: number = 300,
): DecisionCheckRow[] {
  if (checks.length === 0) return checks;
  const bySecond = new Map<number, DecisionCheckRow>();
  const fallbackRows: DecisionCheckRow[] = [];
  for (const row of checks) {
    const sec = checkSecondOffset(row.check_ts, windowStart, windowSeconds);
    if (sec == null) {
      fallbackRows.push(row);
      continue;
    }
    bySecond.set(sec, row);
  }
  const collapsed = Array.from(bySecond.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([, row]) => row);
  return [...collapsed, ...fallbackRows];
}

const ANALYTICS_DETAIL_POLL_MS = 10_000;

/**
 * V3 multi-market: hierarchical slice picker state shared between the
 * Price-feed and Condition-check sections. Each field is the 0-based
 * index into its tier, or ``null`` when not yet picked. The 1-minute
 * field is an OPTIONAL refinement (matching the simulation page): when
 * ``null`` the active slice covers the full 5-min bucket; when set it
 * narrows to 60s. The other tiers are required ONLY when present for
 * the window length (see ``pickTiersForWindow``).
 */
interface SliceState {
  hour: number | null;
  quarter: number | null;
  fiveMin: number | null;
  oneMin: number | null;
}

const EMPTY_SLICE: SliceState = { hour: null, quarter: null, fiveMin: null, oneMin: null };

interface SliceTiers {
  hasHour: boolean;
  hasQuarter: boolean;
  hasFive: boolean;
}

function sliceTiers(windowSeconds: number): SliceTiers {
  const tiers = pickTiersForWindow(windowSeconds);
  return {
    hasHour: tiers.some((t) => t.spanSeconds === 3600),
    hasQuarter: tiers.some((t) => t.spanSeconds === 900),
    hasFive: tiers.some((t) => t.spanSeconds === 300),
  };
}

/**
 * Derive the active slice's [offsetStart, lengthSeconds] from the user's
 * pill selections. Returns ``null`` when the required tiers (the ones
 * present for this window length) haven't all been picked yet, which
 * keeps both consumers from rendering until the user makes a complete
 * selection.
 */
function computeSliceRange(
  windowSeconds: number,
  slice: SliceState,
): { offsetSec: number; lengthSec: number } | null {
  const { hasHour, hasQuarter, hasFive } = sliceTiers(windowSeconds);
  if (hasHour && slice.hour == null) return null;
  if (hasQuarter && slice.quarter == null) return null;
  if (hasFive && slice.fiveMin == null) return null;
  // 5m windows have no outer tiers: 1-min is the only picker AND is
  // required.
  if (!hasHour && !hasQuarter && !hasFive && slice.oneMin == null) return null;
  const h = hasHour ? slice.hour ?? 0 : 0;
  const q = hasQuarter ? slice.quarter ?? 0 : 0;
  const f = hasFive ? slice.fiveMin ?? 0 : 0;
  const m = slice.oneMin ?? 0;
  const offsetSec = h * 3600 + q * 900 + f * 300 + m * 60;
  // Length: 60s when refined to 1-min; otherwise the smallest required
  // outer tier span (300 / 900 / 3600 / window).
  const lengthSec = slice.oneMin != null
    ? LEAF_SPAN_SECONDS
    : hasFive
      ? 300
      : hasQuarter
        ? 900
        : hasHour
          ? 3600
          : windowSeconds;
  return { offsetSec, lengthSec };
}

function DualPriceValue({ ours, poly, signed }: { ours: string | null; poly: string | null; signed?: boolean }) {
  const showPoly = poly != null && poly !== ours;
  return (
    <Group gap={6} wrap="nowrap" align="center">
      <Decimal value={ours} prefix="$" signed={signed} />
      {showPoly ? (
        <Text size="sm" c="dimmed" style={monoStyle}>
          (<Decimal value={poly} prefix="$" signed={signed} />)
        </Text>
      ) : null}
    </Group>
  );
}

export function AnalyticsDetailPane({ slug }: Props) {
  const [refetchOpen, setRefetchOpen] = useState(false);
  const [editOpen, setEditOpen]       = useState(false);
  const [hideOpen, setHideOpen]       = useState(false);
  // V3 multi-market: shared hierarchical slice picker state. Lives at
  // the pane level so the Price-feed pills and the Condition-check
  // table see the SAME selection -- picking "Hour 2 -> 15-min #1 ->
  // 5-min #2" filters both tables to that 5-min window in lockstep.
  // ``null`` everywhere on mount; auto-populated to the first
  // populated leaf once ticks arrive (see effect below).
  const [slice, setSlice] = useState<SliceState>(EMPTY_SLICE);
  // Per-tick decision checks are opt-in: the live bot only persists a
  // single decision_checks row (the moment the order was placed), so by
  // default the section renders that row alone. Clicking the "Simulate
  // checks" button in the section header synthesizes the full per-tick
  // feed from the recorded ticks + window config for the currently
  // selected slice. Reset back to off on window switch so the operator
  // doesn't accidentally leave a heavy synthesis running when scanning
  // through many windows.
  const [simulateChecks, setSimulateChecks] = useState(false);
  useEffect(() => { setSimulateChecks(false); }, [slug]);
  const detailQ = useQuery({
    queryKey: ["analytics", "window", slug],
    queryFn: () => api.analytics.detail(slug),
    staleTime: 30_000,
    refetchInterval: ANALYTICS_DETAIL_POLL_MS,
    refetchIntervalInBackground: true,
  });
  const ticksQ = useQuery({
    queryKey: ["analytics", "window", slug, "ticks"],
    queryFn: () => api.windows.ticks(slug),
    staleTime: 60_000,
  });
  const checksQ = useQuery({
    queryKey: ["analytics", "window", slug, "checks"],
    queryFn: () => api.windows.checks(slug),
    staleTime: 60_000,
  });
  // Use the config bound to THIS window (not the currently active one) so
  // the rendered checks reflect exactly what the live bot evaluated.
  // For data-only windows the stored config_version_id is a NOT-NULL
  // filler that doesn't reflect what the bot actually did — skip the
  // fetch entirely so we don't accidentally render or synthesize off
  // it.
  const isDataOnly = detailQ.data?.is_data_only ?? false;
  const windowCfgId = detailQ.data?.config_version_id ?? null;
  const cfgQ = useQuery({
    queryKey: ["config", "byId", windowCfgId ?? "none"],
    queryFn: () => api.config.byId(windowCfgId as string),
    // Also enable the fetch when the operator opts into check
    // simulation on a data-only window -- the filler config still
    // provides enough for synthesizing a reasonable per-tick feed.
    enabled: windowCfgId != null && (!isDataOnly || simulateChecks),
    staleTime: 5 * 60_000,
  });

  const cfgRecord = (cfgQ.data?.config ?? null) as Record<string, unknown> | null;
  const resolvedCfg = useMemo(() => resolveDecisionConfig(cfgRecord), [cfgRecord]);
  const ticksItems = ticksQ.data?.items ?? [];
  const persistedChecks = checksQ.data?.items ?? [];
  const hasPersistedChecks = persistedChecks.length > 0;
  const rawComputedChecks = useMemo(() => {
    if (!detailQ.data) return [] as DecisionCheckRow[];
    // Per-tick decision checks are opt-in. On load we render nothing;
    // the operator clicks "Simulate checks for selected slice" to
    // populate the table. Persisted decision_checks rows written by
    // the live bot are still preferred on their exact second (ground
    // truth) once simulation is enabled.
    if (!simulateChecks) return [] as DecisionCheckRow[];
    // Prefer persisted rows outright when we can't synthesize (no
    // config loaded yet, or synthesis would produce nothing).
    if (!cfgRecord) return persistedChecks;
    const synthesized = synthesizeDecisionChecks(detailQ.data, ticksItems, cfgRecord, { fullTrace: true });
    if (!hasPersistedChecks) return synthesized;
    // Merge: persisted rows win on their exact second, synthesized
    // rows fill the rest so the operator sees a full per-tick feed.
    const persistedKeys = new Set<number>();
    for (const row of persistedChecks) {
      const ms = Date.parse(row.check_ts);
      if (Number.isFinite(ms)) persistedKeys.add(Math.floor(ms / 1000));
    }
    const merged: DecisionCheckRow[] = [];
    for (const row of synthesized) {
      const ms = Date.parse(row.check_ts);
      if (Number.isFinite(ms) && persistedKeys.has(Math.floor(ms / 1000))) continue;
      merged.push(row);
    }
    merged.push(...persistedChecks);
    merged.sort((a, b) => Date.parse(a.check_ts) - Date.parse(b.check_ts));
    return merged;
  }, [detailQ.data, ticksItems, cfgRecord, hasPersistedChecks, persistedChecks, simulateChecks]);
  const computedChecks = useMemo(
    () => enrichChecksWithMomentum(rawComputedChecks, ticksItems, resolvedCfg),
    [rawComputedChecks, ticksItems, resolvedCfg],
  );
  const visibleChecks = useMemo(
    () => (simulateChecks
      ? computedChecks
      : trimChecksAfterExecution(
        computedChecks,
        detailQ.data?.bet?.bet_status ?? null,
        detailQ.data?.bet?.created_at ?? null,
        detailQ.data?.no_fill_attempt_timestamps ?? [],
      )),
    [
      computedChecks,
      simulateChecks,
      detailQ.data?.bet?.bet_status,
      detailQ.data?.bet?.created_at,
      detailQ.data?.no_fill_attempt_timestamps,
    ],
  );

  // V3 multi-market: reset the slice picker whenever the user navigates
  // to a different window. Without this, a stale "Hour 3 -> 15-min #2"
  // selection from the previous 4h window would carry over into a 5m
  // window that doesn't have those tiers, leaving the picker stuck.
  useEffect(() => { setSlice(EMPTY_SLICE); }, [slug]);
  // Slice always resolves to a single 1-minute leaf. On window switch
  // we default every tier to its first bucket so the price feed and
  // condition-check simulation stay cheap; the operator can drill into
  // a different minute by clicking pills.
  const windowSecondsForSlice = detailQ.data?.window_seconds ?? null;
  useEffect(() => {
    if (windowSecondsForSlice == null) return;
    if (slice.hour != null || slice.quarter != null || slice.fiveMin != null || slice.oneMin != null) return;
    const { hasHour, hasQuarter, hasFive } = sliceTiers(windowSecondsForSlice);
    setSlice({
      hour: hasHour ? 0 : null,
      quarter: hasQuarter ? 0 : null,
      fiveMin: hasFive ? 0 : null,
      oneMin: 0,
    });
  }, [windowSecondsForSlice, slice]);

  if (detailQ.isLoading) return <LoadingState />;
  if (detailQ.isError) return <ErrorState error={detailQ.error as Error} onRetry={() => detailQ.refetch()} />;
  const d = detailQ.data;
  if (!d) return null;
  const checksLoading = checksQ.isLoading || (simulateChecks && cfgQ.isLoading && !cfgRecord);

  return (
    <Stack gap="md">
      <DetailHeader detail={d} />

      <WindowAnalyticsSection detail={d} />

      <Group gap="xs">
        <Button
          size="sm"
          leftSection={<IconRefresh size={14} />}
          variant="light"
          onClick={() => setRefetchOpen(true)}
        >Re-fetch outcome</Button>
        <Button
          size="sm"
          leftSection={<IconEdit size={14} />}
          variant="default"
          onClick={() => setEditOpen(true)}
          title={d.bet ? "Edit order fields" : "Edit window outcome fields"}
        >Edit</Button>
        <Button
          size="sm"
          leftSection={d.hidden ? <IconEye size={14} /> : <IconEyeOff size={14} />}
          variant="default"
          color={d.hidden ? "green" : "red"}
          onClick={() => setHideOpen(true)}
        >{d.hidden ? "Unhide" : "Hide"} window</Button>
      </Group>

      <MarketSection detail={d} config={cfgRecord} />
      <WindowPreOrderMetricsSection detail={d} />
      {d.bet ? <BetSection detail={d} /> : null}
      <ProbabilityTrackingSection rows={d.probability_timing} />
      <PriceFeedSection
        slug={slug}
        windowStart={d.window_start}
        windowSeconds={d.window_seconds}
        bucketSeconds={d.bucket_seconds}
        ticks={ticksQ.data?.items ?? []}
        loading={ticksQ.isLoading}
        slice={slice}
        onSliceChange={setSlice}
      />
      {/* V3 data-only markets never ran decision checks; suppress
          the entire section so the operator isn't shown synthetic
          rows derived from an unrelated market's config. */}
      {d.is_data_only ? null : <ConditionCheckSection
        checks={visibleChecks}
        ticks={ticksItems}
        windowStart={d.window_start}
        windowSeconds={d.window_seconds}
        loading={checksLoading}
        synthetic={simulateChecks}
        betStatus={d.bet?.bet_status ?? null}
        executionTs={d.bet?.created_at ?? null}
        noFillAttemptTimestamps={d.no_fill_attempt_timestamps ?? []}
        minOdds={resolvedCfg.min_odds}
        maxOdds={resolvedCfg.max_odds}
        minMatchingCandles={resolvedCfg.min_matching_candles}
        momentumEnabled={Boolean(cfgRecord?.odds_momentum_enabled ?? false)}
        prevWindowsEnabled={Boolean(cfgRecord?.prev_windows_check_enabled ?? false)}
        kronosEnabled={Boolean(cfgRecord?.kronos_check_enabled ?? false)}
        slice={slice}
        simulateEnabled={simulateChecks}
        onToggleSimulate={() => setSimulateChecks((v) => !v)}
      />}

      <RefetchOutcomeModal slug={slug} opened={refetchOpen} onClose={() => setRefetchOpen(false)} />
      <EditOrderModal       detail={d} opened={editOpen}    onClose={() => setEditOpen(false)} />
      <HideWindowConfirm slug={d.slug} hidden={d.hidden} opened={hideOpen} onClose={() => setHideOpen(false)} />
    </Stack>
  );
}

// ----------------------------------------------------------------- Header

function DetailHeader({ detail }: { detail: AnalyticsWindowDetail }) {
  const start = new Date(detail.window_start);
  const end = new Date(detail.window_end);
  const fmt = (d: Date) =>
    `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  // V3 multi-market: the DB stores the bot's synthetic slug, but
  // Polymarket indexes each window under `slug_pattern` from the
  // catalogue (e.g. `eth-updown-5m-{epoch}`). Without translation the
  // link 404s for ETH and BTC 1h windows.
  const { markets } = useMarket();
  const detailMarket = markets.find((m) => m.market_id === detail.market_id) ?? null;
  const polymarketUrl = polymarketEventUrl({
    slug: detail.slug,
    slugPattern: detailMarket?.slug_pattern ?? null,
    windowStartIso: detail.window_start,
  });
  const delta = detail.btc_delta ? Number(detail.btc_delta) : null;
  return (
    <Group justify="space-between" align="flex-start" wrap="nowrap">
      <Group gap="lg" align="center" wrap="nowrap">
        <Title order={3} style={monoStyle}>{fmt(start)}–{fmt(end).slice(-5)}</Title>
        {delta != null ? (
          <Text
            size="lg"
            fw={600}
            style={{ ...monoStyle, color: delta >= 0 ? "var(--mantine-color-profit-5)" : "var(--mantine-color-loss-5)" }}
          >
            {delta >= 0 ? "↑" : "↓"} ${Math.abs(delta).toFixed(2)}
          </Text>
        ) : null}
        <BetStatusBadge status={detail.bet_status} bet_direction={detail.bet_direction} pnl={detail.pnl_usdc} />
        {detail.is_manual_buy ? <Badge size="lg" radius="xl" color="orange" variant="filled">Manual Buy</Badge> : null}
        {detail.counterpart_override_active ? (
          <Badge
            size="lg"
            radius="xl"
            color="orange"
            variant="filled"
            leftSection={<IconRefresh size={14} />}
            title="Counterpart Override or Follow mode was active for this order's entry decision. Only configured gates were eligible for bypass."
          >
            Counterpart Override
          </Badge>
        ) : null}
        {detail.favorite_hold_tier ? (
          <Badge
            size="lg"
            radius="xl"
            color="teal"
            variant="filled"
            leftSection={<IconAnchor size={14} />}
            title="This order was entered via the Favorite Hold feature (opening favourite never flipped)."
          >
            Favorite Hold Tier {detail.favorite_hold_tier.replace("tier", "")}
          </Badge>
        ) : null}
        {detail.hidden ? <Badge size="lg" radius="xl" color="gray" variant="filled">Hidden</Badge> : null}
        <ExitReasonBadge reason={detail.bet?.exit_reason ?? null} soldEarly={detail.sold_early} />
      </Group>
      <Anchor href={polymarketUrl} target="_blank" size="sm" c="cyan">
        View on Polymarket <IconExternalLink size={12} style={{ verticalAlign: "middle" }} />
      </Anchor>
    </Group>
  );
}

/**
 * Surfaces the bot's recorded exit cause when a position was closed
 * before window resolution. `reason` comes from the new orders.exit_reason
 * column (V2 migration 0003); historical rows that pre-date the column
 * but were flagged sold_early=1 still get a generic "Sold early" pill so
 * they're visibly distinct from natural-resolution wins/losses.
 */
function ExitReasonBadge({ reason, soldEarly }: {
  reason: "take_profit" | "stop_loss" | "manual" | "momentum_sell" | null;
  soldEarly: boolean;
}) {
  if (!soldEarly && !reason) return null;
  const meta: Record<string, { label: string; color: string }> = {
    take_profit: { label: "Take-profit", color: "teal" },
    stop_loss:   { label: "Stop-loss",   color: "orange" },
    manual:      { label: "Manual sell", color: "grape" },
    momentum_sell: { label: "Momentum sell", color: "orange" },
  };
  const m = reason ? meta[reason] : null;
  const label = m?.label ?? "Sold early";
  const color = m?.color ?? "orange";
  return <Badge size="lg" radius="xl" color={color}>{label}</Badge>;
}

function BetStatusBadge({ status, bet_direction, pnl }:
  { status: string; bet_direction: "UP" | "DOWN" | null; pnl: string | null }) {
  // Sold-early positions still have a realised win or loss — show the
  // operator that, not just a generic "Sold Early" label. (The exit
  // cause itself surfaces via the adjacent ExitReasonBadge.)
  const effective: string = (() => {
    if (status !== "sold_early") return status;
    if (pnl == null) return status;
    const n = Number(pnl);
    if (!Number.isFinite(n)) return status;
    return n >= 0 ? "won" : "lost";
  })();
  const color =
    effective === "won" ? "green"
    : effective === "lost" ? "red"
    : effective === "no_fill" ? "gray"
    : effective === "skipped" ? "yellow"
    : effective === "sold_early" ? "orange"
    : effective === "pending" ? "blue"
    : "dark";
  const label = effective === "no_bet"
    ? "No bet"
    : effective === "won" && bet_direction
    ? `Won: ${bet_direction} ${pnl ? `(${signed(pnl)})` : ""}`
    : effective === "lost" && bet_direction
    ? `Lost: ${bet_direction} ${pnl ? `(${signed(pnl)})` : ""}`
    : prettyStatus(effective);
  return <Badge size="lg" radius="xl" color={color} variant="light">{label}</Badge>;
}

function prettyStatus(s: string) {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function signed(v: string | null): string {
  if (v == null) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `${n >= 0 ? "+" : "-"}$${Math.abs(n).toFixed(2)}`;
}

// ------------------------------------------------------------ Window Analytics

function WindowAnalyticsSection({ detail }: { detail: AnalyticsWindowDetail }) {
  const minutes = detail.minutes;
  return (
    <Card withBorder padding="md">
      <Group justify="space-between" align="baseline" mb="sm">
        <Title order={5}>Window analytics ({detail.sample_count} samples)</Title>
        <Text size="sm" c="dimmed">BTC minute-by-minute</Text>
      </Group>
      <Group gap="md" mb="md">
        {minutes.map((m) => (
          <MinuteBubble key={m.minute_index} minute={m} />
        ))}
      </Group>
      <Divider mb="sm" />
      <Text size="sm" c="dimmed" mb={4}>Per-minute detail</Text>
      <Table withTableBorder horizontalSpacing="sm" verticalSpacing={6} style={monoStyle} fz="sm">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>M</Table.Th>
            <Table.Th>BTC OPEN</Table.Th>
            <Table.Th>CLOSE</Table.Th>
            <Table.Th>HIGH</Table.Th>
            <Table.Th>LOW</Table.Th>
            <Table.Th>UP HIGH BID</Table.Th>
            <Table.Th>UP LOW BID</Table.Th>
            <Table.Th>DOWN HIGH BID</Table.Th>
            <Table.Th>DOWN LOW BID</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {minutes.map((m) => (
            <Table.Tr key={m.minute_index}>
              <Table.Td>M{m.minute_index}</Table.Td>
              <Table.Td><Decimal value={m.btc_open} /></Table.Td>
              <Table.Td><Decimal value={m.btc_close} /></Table.Td>
              <Table.Td><Decimal value={m.btc_high} /></Table.Td>
              <Table.Td><Decimal value={m.btc_low} /></Table.Td>
              <Table.Td><Decimal value={m.up_high_bid} digits={3} /></Table.Td>
              <Table.Td><Decimal value={m.up_low_bid} digits={3} /></Table.Td>
              <Table.Td><Decimal value={m.down_high_bid} digits={3} /></Table.Td>
              <Table.Td><Decimal value={m.down_low_bid} digits={3} /></Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Card>
  );
}

function MinuteBubble({ minute }: { minute: MinuteAggregate }) {
  const open = Number(minute.btc_open);
  const close = Number(minute.btc_close);
  const delta = Number.isFinite(open) && Number.isFinite(close) ? close - open : 0;
  const positive = delta >= 0;
  return (
    <Badge
      size="lg"
      color={positive ? "profit" : "loss"}
      variant="light"
      style={{ ...monoStyle, padding: "8px 12px", fontSize: 16 }}
      radius="md"
    >
      M{minute.minute_index} {positive ? "↑" : "↓"} {positive ? "+" : "-"}${Math.abs(delta).toFixed(2)}
    </Badge>
  );
}

// ------------------------------------------------------------------ Market

function MarketSection({ detail, config }: { detail: AnalyticsWindowDetail; config: Record<string, unknown> | null }) {
  const polyMetaQ = useQuery({
    queryKey: ["analytics", "window", detail.slug, "refetch-outcome"],
    queryFn: () => api.analytics.refetchOutcome(detail.slug),
    staleTime: 60_000,
    retry: false,
  });
  const proposed = polyMetaQ.data?.proposed;
  const proposedStart = proposed?.btc_start ?? null;
  const proposedEnd = proposed?.btc_end ?? null;
  const proposedDelta = proposed?.btc_delta ?? null;
  const orderType = String(config?.order_type ?? "FOK").toUpperCase();
  const gtcAutoCancel = Boolean(config?.gtc_auto_cancel_on_drift ?? true);
  const gtcRecheck = Number(config?.gtc_recheck_seconds ?? 2);
  const gtcCancelAfter = Number(config?.gtc_cancel_unfilled_after_seconds ?? 15);
  const openPriceGateEnabled = Boolean(config?.open_price_gate_enabled ?? false);
  const openPriceOverrideEnabled = Boolean(config?.open_price_override_enabled ?? false);
  const openPriceOverrideWithin = Number(config?.open_price_override_within_seconds ?? 0);
  const openPriceCushionEnabled = Boolean(config?.open_price_cushion_enabled ?? false);
  const openPriceCushionMinBps = Number(config?.open_price_cushion_min_bps ?? 2);
  const prevWindowsEnabled = Boolean(config?.prev_windows_check_enabled ?? false);
  const prevWindowsCount = Number(config?.prev_windows_count ?? 3);
  const prevWindowsMinMatches = Number(config?.prev_windows_min_matches ?? 2);
  const kronosEnabled = Boolean(config?.kronos_check_enabled ?? false);
  const kronosRequireDir = Boolean(config?.kronos_require_direction ?? true);
  const kronosMinConf = Number(config?.kronos_min_confidence ?? 0.5);
  const kronosModel = String(config?.kronos_model ?? "small");

  return (
    <Card withBorder padding="md">
      <Title order={5} mb="xs">Market</Title>
      <Table withTableBorder={false} horizontalSpacing="sm" verticalSpacing={6} style={monoStyle} fz="sm">
        <Table.Tbody>
          <Row
            label="BTC start"
            value={<DualPriceValue ours={detail.btc_open} poly={proposedStart} signed={false} />}
          />
          <Row
            label="BTC end"
            value={<DualPriceValue ours={detail.btc_close} poly={proposedEnd} signed={false} />}
          />
          <Row
            label="BTC delta"
            value={<DualPriceValue ours={detail.btc_delta} poly={proposedDelta} signed />}
          />
          <Row label="Winning direction" value={
            detail.winning_direction
              ? <Badge color={detail.winning_direction === "UP" ? "profit" : "loss"}>{detail.winning_direction}</Badge>
              : <Text c="dimmed" size="sm">—</Text>
          } />
          <Row label="Outcome source" value={<Text size="sm" c="dimmed">{detail.outcome_source ?? "—"}</Text>} />
          <Row label="Feed source"    value={<Text size="sm" c="dimmed">{detail.feed_source ?? "—"}</Text>} />
          {/* V3 data-only markets: hide every trading-config-derived
              row (Order type, GTC behaviour, Config version). The
              stored config_version_id is a NOT-NULL filler — usually
              the legacy btc-5m global — and showing it here would
              imply the bot evaluated this window against that config,
              which it did not. */}
          {detail.is_data_only ? (
            <Row
              label="Config"
              value={
                <Badge size="sm" variant="light" color="gray">
                  Data-collection only
                </Badge>
              }
            />
          ) : (
            <>
              <Row label="Order type" value={<Text size="sm">{orderType}</Text>} />
              <Row
                label="Open-price gate"
                value={
                  openPriceGateEnabled ? (
                    <Text size="sm">Current must be on picked side of open</Text>
                  ) : (
                    <Text size="sm" c="dimmed">Disabled</Text>
                  )
                }
              />
              <Row
                label="Open-price candle override"
                value={
                  openPriceOverrideEnabled ? (
                    <Text size="sm">
                      {openPriceOverrideWithin > 0
                        ? `last ${openPriceOverrideWithin}s if price confirms`
                        : "right away if price confirms"}
                    </Text>
                  ) : (
                    <Text size="sm" c="dimmed">Disabled</Text>
                  )
                }
              />
              <Row
                label="Open-price cushion gate"
                value={
                  openPriceCushionEnabled ? (
                    <Text size="sm">Current must be &ge; {openPriceCushionMinBps} bps past open</Text>
                  ) : (
                    <Text size="sm" c="dimmed">Disabled</Text>
                  )
                }
              />
              <Row
                label="Previous windows gate"
                value={
                  prevWindowsEnabled ? (
                    <Text size="sm">
                      {prevWindowsMinMatches} of last {prevWindowsCount} closed same-direction
                    </Text>
                  ) : (
                    <Text size="sm" c="dimmed">Disabled</Text>
                  )
                }
              />
              <Row
                label="Kronos forecast gate"
                value={
                  kronosEnabled ? (
                    <Text size="sm">
                      {kronosModel}: prob ≥ {kronosMinConf.toFixed(2)}
                      {kronosRequireDir ? ", direction must match" : ""}
                    </Text>
                  ) : (
                    <Text size="sm" c="dimmed">Disabled</Text>
                  )
                }
              />
              <Row label="GTC auto-cancel on drift" value={<Text size="sm">{gtcAutoCancel ? "Enabled" : "Disabled"}</Text>} />
              {!gtcAutoCancel ? (
                <>
                  <Row label="GTC recheck interval" value={<Text size="sm">{Number.isFinite(gtcRecheck) ? `${gtcRecheck}s` : "—"}</Text>} />
                  <Row label="GTC cancel unfilled after" value={<Text size="sm">{Number.isFinite(gtcCancelAfter) ? `${gtcCancelAfter}s` : "—"}</Text>} />
                </>
              ) : null}
              <Row
                label="Config version"
                value={
                  <Anchor
                    href={`#/analytics/manual-simulator?config_version_id=${encodeURIComponent(detail.config_version_id)}`}
                    target="_blank"
                    rel="noreferrer"
                    title="Open this config in the Manual Simulator (new tab)"
                  >
                    <Code style={monoStyle}>{detail.config_version_id}</Code>
                  </Anchor>
                }
              />
            </>
          )}
        </Table.Tbody>
      </Table>
    </Card>
  );
}

// --------------------------------------------------------------------- Bet

function BetSection({ detail }: { detail: AnalyticsWindowDetail }) {
  const bet = detail.bet!;
  return (
    <Card withBorder padding="md">
      <Group justify="space-between" mb="xs">
        <Title order={5}>Bet</Title>
        <Group gap="xs">
          <Badge variant="filled" color={bet.mode === "live" ? "cyan" : "grape"}>{bet.mode.toUpperCase()}</Badge>
          <BetStatusBadge status={bet.bet_status} bet_direction={bet.direction} pnl={bet.pnl_usdc} />
        </Group>
      </Group>
      <Table withTableBorder={false} horizontalSpacing="sm" verticalSpacing={6} style={monoStyle} fz="sm">
        <Table.Tbody>
          <Row label="Direction" value={<Badge color={bet.direction === "UP" ? "profit" : "loss"}>{bet.direction}</Badge>} />
          <Row label="Status (raw)" value={<Text size="sm">{bet.status}</Text>} />
          <Row label="Size (USDC)" value={<Decimal value={bet.size_usdc} prefix="$" />} />
          <Row label="Price"       value={<Decimal value={bet.price} digits={4} />} />
          <Row label="Shares"      value={<Decimal value={bet.size_shares ?? bet.filled_shares} digits={4} />} />
          <Row label="Fee (USDC)"  value={<Decimal value={bet.fee_usdc} prefix="$" digits={4} />} />
          <Row label="Net P/L"     value={<Decimal value={bet.pnl_usdc} prefix="$" signed />} />
          <Row label="Bought at"   value={<Text size="sm">{new Date(bet.created_at).toLocaleString()}</Text>} />
          <Row label="Exit at"     value={<Text size="sm">{bet.resolved_at ? new Date(bet.resolved_at).toLocaleString() : "—"}</Text>} />
          {bet.sold_early || bet.exit_reason ? (
            <Row label="Exit reason" value={<ExitReasonBadge reason={bet.exit_reason} soldEarly={bet.sold_early} />} />
          ) : null}
          <Row label="Order ID"    value={<Code style={monoStyle}>{bet.order_id}</Code>} />
          {bet.exchange_order_id
            ? <Row label="Exchange order ID" value={<Code style={monoStyle}>{bet.exchange_order_id}</Code>} />
            : null}
        </Table.Tbody>
      </Table>
      <PreOrderMetricsBlock bet={bet} />
    </Card>
  );
}

type PreOrderMetricsSnapshot = NonNullable<AnalyticsWindowDetail["pre_order_metrics_window"]>;

function PreOrderMetricsTable({
  snapshot,
  title,
  direction,
}: {
  snapshot: PreOrderMetricsSnapshot;
  title: string;
  direction?: "UP" | "DOWN" | null;
}) {
  const m = snapshot;
  const fmt4 = (v: number | null | undefined) =>
    v == null || !Number.isFinite(Number(v)) ? "—" : Number(v).toFixed(4);
  const fmtPct = (v: number | null | undefined) =>
    v == null || !Number.isFinite(Number(v)) ? "—" : `${Number(v).toFixed(4)}%`;
  return (
    <>
      {title ? <Title order={6} mt="md" mb="xs">{title}</Title> : null}
      <Table withTableBorder={false} horizontalSpacing="sm" verticalSpacing={6} style={monoStyle} fz="sm">
        <Table.Tbody>
          <Row label="Samples observed"
               value={<Text size="sm">{m.sample_count ?? 0}</Text>} />
          <Row label="Avg UP mid" value={<Text size="sm">{fmt4(m.avg_up_mid)}</Text>} />
          <Row label="Avg DOWN mid" value={<Text size="sm">{fmt4(m.avg_down_mid)}</Text>} />
          <Row label="Lowest UP mid" value={<Text size="sm">{fmt4(m.up_min_mid)}</Text>} />
          <Row label="Highest UP mid" value={<Text size="sm">{fmt4(m.up_max_mid)}</Text>} />
          <Row label="Lowest DOWN mid" value={<Text size="sm">{fmt4(m.down_min_mid)}</Text>} />
          <Row label="Highest DOWN mid" value={<Text size="sm">{fmt4(m.down_max_mid)}</Text>} />
          <Row label="BTC open"
               value={<Text size="sm">{m.btc_open == null ? "—" : Number(m.btc_open).toFixed(2)}</Text>} />
          <Row label="Lowest BTC Δ%" value={<Text size="sm">{fmtPct(m.btc_min_pct)}</Text>} />
          <Row label="Highest BTC Δ%" value={<Text size="sm">{fmtPct(m.btc_max_pct)}</Text>} />
        </Table.Tbody>
      </Table>
    </>
  );
}

function PreOrderMetricsBlock({ bet }: { bet: NonNullable<AnalyticsWindowDetail["bet"]> }) {
  const m = bet.pre_order_metrics;
  if (!m) return null;
  return (
    <PreOrderMetricsTable
      snapshot={m as PreOrderMetricsSnapshot}
      title="Pre-order metrics (up to fill)"
      direction={bet.direction}
    />
  );
}

function WindowPreOrderMetricsSection({ detail }: { detail: AnalyticsWindowDetail }) {
  const m = detail.pre_order_metrics_window;
  if (!m) return null;
  return (
    <Card withBorder padding="md">
      <Title order={5} mb={4}>Pre-order Metrics (whole window)</Title>
      <Text size="xs" c="dimmed" mb="xs">
        Rolling averages + extremes of odds mid and BTC Δ% across every tick in the window. These are the values the
        Pre-order Metrics Filter would evaluate against for a placement.
      </Text>
      <PreOrderMetricsTable
        snapshot={m as PreOrderMetricsSnapshot}
        title=""
        direction={detail.bet_direction ?? null}
      />
    </Card>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Table.Tr>
      <Table.Td style={{ width: 180, color: "var(--mantine-color-dimmed)" }}>{label}</Table.Td>
      <Table.Td>{value}</Table.Td>
    </Table.Tr>
  );
}

// ----------------------------------------------------- Probability Tracking

function ProbabilityTrackingSection({ rows }: { rows: ProbabilityTiming[] }) {
  const up = rows.find((r) => r.side === "UP");
  const dn = rows.find((r) => r.side === "DOWN");
  return (
    <Card withBorder padding="md">
      <Title order={5} mb="xs">Probability tracking</Title>
      <Group gap="lg" mb="md">
        <SmallStat label="Min prob observed (UP)" value={up?.min_prob_observed ?? null} />
        <SmallStat label="Min prob observed (DOWN)" value={dn?.min_prob_observed ?? null} />
      </Group>
      <Table withTableBorder horizontalSpacing="sm" verticalSpacing={6} style={monoStyle} fz="sm">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Event</Table.Th>
            <Table.Th>UP first</Table.Th>
            <Table.Th>UP last</Table.Th>
            <Table.Th>DN first</Table.Th>
            <Table.Th>DN last</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          <TimingRow label="≥ min_odds" up_first={up?.first_min_offset} up_last={up?.last_min_offset} dn_first={dn?.first_min_offset} dn_last={dn?.last_min_offset} />
          <TimingRow label="≥ max_odds" up_first={up?.first_max_offset} up_last={up?.last_max_offset} dn_first={dn?.first_max_offset} dn_last={dn?.last_max_offset} />
          <TimingRow label="< stop_loss" up_first={up?.first_stop_loss_offset} up_last={up?.last_stop_loss_offset} dn_first={dn?.first_stop_loss_offset} dn_last={dn?.last_stop_loss_offset} />
          <TimingRow label="≤ 50%" up_first={up?.first_below_50_offset} up_last={up?.last_below_50_offset} dn_first={dn?.first_below_50_offset} dn_last={dn?.last_below_50_offset} />
        </Table.Tbody>
      </Table>
    </Card>
  );
}

function TimingRow({ label, up_first, up_last, dn_first, dn_last }:
  { label: string; up_first?: number | null; up_last?: number | null; dn_first?: number | null; dn_last?: number | null }) {
  return (
    <Table.Tr>
      <Table.Td>{label}</Table.Td>
      <Table.Td>{fmtOffset(up_first)}</Table.Td>
      <Table.Td>{fmtOffset(up_last)}</Table.Td>
      <Table.Td>{fmtOffset(dn_first)}</Table.Td>
      <Table.Td>{fmtOffset(dn_last)}</Table.Td>
    </Table.Tr>
  );
}

function fmtOffset(offset: number | null | undefined): string {
  if (offset == null) return "—";
  const m = Math.floor(offset / 60);
  const s = offset % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function SmallStat({ label, value }: { label: string; value: string | null }) {
  return (
    <Stack gap={0}>
      <Text size="sm" c="dimmed">{label}</Text>
      <Text fw={700} size="lg" style={monoStyle}>{value ?? "—"}</Text>
    </Stack>
  );
}

// ---------------------------------------------------------------- Price Feed

/**
 * V3 multi-market: the Price-feed table nests tabs all the way from
 * the largest applicable tier (1-hr for btc-4h) down to a 1-minute
 * leaf showing 60 per-second rows. Layout per market:
 *
 *   btc-5m  (300s)   ->                                    5 × 1-min
 *   btc-15m (900s)   ->                       3 × 5-min ->  5 × 1-min
 *   btc-1h  (3600s)  ->          4 × 15-min × 3 × 5-min ->  5 × 1-min
 *   btc-4h  (14400s) -> 4 × 1h × 4 × 15-min × 3 × 5-min ->  5 × 1-min
 *
 * The leaf is always 60 seconds wide regardless of the market's
 * nominal ``bucket_seconds`` (which is now only used by the trading-bot
 * for decision bucketing, not for this presentation layer).
 *
 * All bucket arithmetic lives in ``pricefeedHierarchy.ts``; this
 * component just renders the tree.
 */
function PriceFeedSection({
  slug,
  windowStart,
  windowSeconds,
  bucketSeconds,
  ticks,
  loading,
  slice,
  onSliceChange,
}: {
  slug: string;
  windowStart: string;
  /** V3 multi-market: total window length (e.g. 900 for 15m, 14400 for 4h). */
  windowSeconds: number;
  /** Catalogue-level decision bucket span; surfaced as a footer hint. */
  bucketSeconds: number;
  ticks: WindowTick[];
  loading: boolean;
  slice: SliceState;
  onSliceChange: (next: SliceState) => void;
}) {
  void slug;

  // Index every tick by its 1-second offset from window_start. This is
  // the single source of truth for both leaf rendering and per-pill
  // tick-count suffixes.
  const ticksBySecond = useMemo(() => {
    const map = new Map<number, WindowTick>();
    if (ticks.length === 0) return map;
    const sorted = ticks.slice().sort(
      (a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime(),
    );
    for (const t of sorted) {
      const offset = checkSecondOffset(t.ts, windowStart, windowSeconds);
      if (offset == null) continue;
      map.set(offset, t);
    }
    return map;
  }, [ticks, windowStart, windowSeconds]);

  const tickCountInRange = (start: number, end: number): number => {
    let n = 0;
    for (let s = start; s < end; s += 1) if (ticksBySecond.has(s)) n += 1;
    return n;
  };

  const tiers = pickTiersForWindow(windowSeconds);
  const { hasHour, hasQuarter, hasFive } = sliceTiers(windowSeconds);
  const tierHint = tiers.length === 0
    ? `${windowSeconds / LEAF_SPAN_SECONDS} 1-minute buckets`
    : (() => {
        const parts: string[] = [];
        let prevSpan = windowSeconds;
        for (const t of tiers) {
          const n = Math.floor(prevSpan / t.spanSeconds);
          const label = t.spanSeconds === 3600
            ? `${n} × 1-hr`
            : `${n} × ${t.spanSeconds / 60}-min`;
          parts.push(label);
          prevSpan = t.spanSeconds;
        }
        const lastN = Math.floor(prevSpan / LEAF_SPAN_SECONDS);
        parts.push(`${lastN} × 1-min`);
        return parts.join(" → ");
      })();

  // Decision-bucket footnote: surfaces the catalogue's bucket_seconds
  // for context (e.g. "decision bucket: 60s") since it no longer
  // controls the table layout but operators may still want to see it.
  const decisionBucketHint = bucketSeconds && bucketSeconds !== LEAF_SPAN_SECONDS
    ? ` · decision bucket: ${bucketSeconds >= 60 && bucketSeconds % 60 === 0
        ? `${bucketSeconds / 60}-min`
        : `${bucketSeconds}s`}`
    : "";

  // Derive per-pill counts at each tier from the user's selections.
  // Hour pills count ticks across the full hour; 15-min pills count
  // ticks across the active hour's quarter; etc. Mirrors the
  // ``s.tickCount`` numbers the old nested-Tabs UI surfaced.
  const hourCounts = useMemo(() => {
    if (!hasHour) return [] as number[];
    const n = Math.floor(windowSeconds / 3600);
    return Array.from({ length: n }, (_, i) => tickCountInRange(i * 3600, (i + 1) * 3600));
  }, [hasHour, windowSeconds, ticksBySecond]);
  const quarterCounts = useMemo(() => {
    if (!hasQuarter) return [] as number[];
    const baseHourSec = hasHour ? (slice.hour ?? 0) * 3600 : 0;
    const hourSpan = hasHour ? 3600 : Math.min(windowSeconds, 3600);
    const n = Math.floor(hourSpan / 900);
    return Array.from({ length: n }, (_, i) => tickCountInRange(baseHourSec + i * 900, baseHourSec + (i + 1) * 900));
  }, [hasQuarter, hasHour, slice.hour, windowSeconds, ticksBySecond]);
  const fiveMinCounts = useMemo(() => {
    if (!hasFive) return [] as number[];
    const baseHourSec = hasHour ? (slice.hour ?? 0) * 3600 : 0;
    const baseQuarterSec = hasQuarter ? (slice.quarter ?? 0) * 900 : 0;
    const quarterSpan = hasQuarter ? 900 : Math.min(windowSeconds, 900);
    const n = Math.floor(quarterSpan / 300);
    const base = baseHourSec + baseQuarterSec;
    return Array.from({ length: n }, (_, i) => tickCountInRange(base + i * 300, base + (i + 1) * 300));
  }, [hasFive, hasQuarter, hasHour, slice.hour, slice.quarter, windowSeconds, ticksBySecond]);
  const oneMinCounts = useMemo(() => {
    const baseHourSec = hasHour ? (slice.hour ?? 0) * 3600 : 0;
    const baseQuarterSec = hasQuarter ? (slice.quarter ?? 0) * 900 : 0;
    const baseFiveSec = hasFive ? (slice.fiveMin ?? 0) * 300 : 0;
    // Parent span for the 1-min pills is the smallest required outer
    // tier (or the whole window when no outer tiers exist, e.g. 5m).
    const parentSpan = hasFive ? 300 : hasQuarter ? 900 : hasHour ? 3600 : windowSeconds;
    const n = Math.floor(parentSpan / 60);
    const base = baseHourSec + baseQuarterSec + baseFiveSec;
    return Array.from({ length: n }, (_, i) => tickCountInRange(base + i * 60, base + (i + 1) * 60));
  }, [hasHour, hasQuarter, hasFive, slice.hour, slice.quarter, slice.fiveMin, windowSeconds, ticksBySecond]);

  const windowStartMs = useMemo(() => Date.parse(windowStart), [windowStart]);
  const range = computeSliceRange(windowSeconds, slice);
  // Pill labels use the wall-clock time at which each bucket STARTS,
  // relative to the window's own start. Falls back to the ordinal
  // ("Hour 1", "M1"...) when we can't parse windowStart so the picker
  // never renders empty strings.
  const pillStartLabel = (offsetSec: number, fallback: string): string => {
    if (!Number.isFinite(windowStartMs)) return fallback;
    const d = new Date(windowStartMs + offsetSec * 1000);
    // Match the price-feed table's toLocaleTimeString formatting for
    // consistency (e.g. "11:02:00 PM"), but drop the seconds since
    // buckets are minute-aligned.
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  };

  // Stacked SegmentedControl rows ("pills"), mirroring the simulation
  // page's hierarchical picker. Each row is keyed off the IMMEDIATE
  // parent's selection so changing "Hour" recomputes the visible
  // 15-min / 5-min / 1-min children automatically.
  return (
    <Card withBorder padding="md">
      <Group justify="space-between" align="baseline" mb="xs">
        <Title order={5}>Price feed (1s)</Title>
        <Text size="xs" c="dimmed">
          {tierHint}
          {decisionBucketHint}
        </Text>
      </Group>
      <Stack gap={4} mb="sm">
        {hasHour ? (
          <Group gap="xs" align="center" wrap="wrap">
            <Text size="xs" c="dimmed" w={56}>Hour</Text>
            <SegmentedControl
              size="xs"
              value={slice.hour == null ? "0" : String(slice.hour)}
              onChange={(v) => {
                // Switching outer tiers always resets lower tiers to
                // their first bucket so the slice stays at 1-min
                // granularity (never expands to a 15-min / hour span).
                onSliceChange({
                  hour: Number(v),
                  quarter: hasQuarter ? 0 : null,
                  fiveMin: hasFive ? 0 : null,
                  oneMin: 0,
                });
              }}
              data={Array.from({ length: hourCounts.length }, (_, i) => ({
                value: String(i),
                label: `${pillStartLabel(i * 3600, `Hour ${i + 1}`)} (${hourCounts[i] ?? 0})`,
              }))}
            />
          </Group>
        ) : null}
        {hasQuarter ? (
          <Group gap="xs" align="center" wrap="wrap">
            <Text size="xs" c="dimmed" w={56}>15-min</Text>
            <SegmentedControl
              size="xs"
              value={slice.quarter == null ? "0" : String(slice.quarter)}
              onChange={(v) => {
                onSliceChange({
                  ...slice,
                  quarter: Number(v),
                  fiveMin: hasFive ? 0 : null,
                  oneMin: 0,
                });
              }}
              data={Array.from({ length: quarterCounts.length }, (_, i) => {
                const baseSec = (hasHour ? (slice.hour ?? 0) * 3600 : 0) + i * 900;
                return {
                  value: String(i),
                  label: `${pillStartLabel(baseSec, `15-min #${i + 1}`)} (${quarterCounts[i] ?? 0})`,
                };
              })}
            />
          </Group>
        ) : null}
        {hasFive ? (
          <Group gap="xs" align="center" wrap="wrap">
            <Text size="xs" c="dimmed" w={56}>5-min</Text>
            <SegmentedControl
              size="xs"
              value={slice.fiveMin == null ? "0" : String(slice.fiveMin)}
              onChange={(v) => {
                onSliceChange({
                  ...slice,
                  fiveMin: Number(v),
                  oneMin: 0,
                });
              }}
              data={Array.from({ length: fiveMinCounts.length }, (_, i) => {
                const baseSec = (hasHour ? (slice.hour ?? 0) * 3600 : 0)
                  + (hasQuarter ? (slice.quarter ?? 0) * 900 : 0)
                  + i * 300;
                return {
                  value: String(i),
                  label: `${pillStartLabel(baseSec, `5-min #${i + 1}`)} (${fiveMinCounts[i] ?? 0})`,
                };
              })}
            />
          </Group>
        ) : null}
        <Group gap="xs" align="center" wrap="wrap">
          <Text size="xs" c="dimmed" w={56}>1-min</Text>
          <SegmentedControl
            size="xs"
            value={slice.oneMin == null ? "0" : String(slice.oneMin)}
            onChange={(v) => onSliceChange({ ...slice, oneMin: Number(v) })}
            data={Array.from({ length: oneMinCounts.length }, (_, i) => {
              const baseSec = (hasHour ? (slice.hour ?? 0) * 3600 : 0)
                + (hasQuarter ? (slice.quarter ?? 0) * 900 : 0)
                + (hasFive ? (slice.fiveMin ?? 0) * 300 : 0)
                + i * 60;
              return {
                value: String(i),
                label: `${pillStartLabel(baseSec, `M${i + 1}`)} (${oneMinCounts[i] ?? 0})`,
              };
            })}
          />
        </Group>
      </Stack>
      {loading ? (
        <LoadingState label="loading ticks…" />
      ) : range == null ? (
        <Text c="dimmed" size="sm">Pick a slice above to load its 1-second price feed.</Text>
      ) : (
        <ScrollArea h={420}>
          <Table withTableBorder horizontalSpacing="sm" verticalSpacing={5} style={monoStyle} fz="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Time</Table.Th>
                <Table.Th>Secs</Table.Th>
                <Table.Th>BTC Price</Table.Th>
                <Table.Th>UP %</Table.Th>
                <Table.Th>DOWN %</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {Array.from({ length: range.lengthSec }, (_, idx) => {
                const offsetSec = range.offsetSec + idx;
                const t = ticksBySecond.get(offsetSec) ?? null;
                const rowTimeLabel = Number.isFinite(windowStartMs)
                  ? new Date(windowStartMs + offsetSec * 1000).toLocaleTimeString()
                  : "—";
                const secsLeftLabel = `${Math.max(0, windowSeconds - offsetSec)}s`;
                return (
                  <Table.Tr key={offsetSec} style={t ? undefined : { opacity: 0.45 }}>
                    <Table.Td>{rowTimeLabel}</Table.Td>
                    <Table.Td>{t ? `${t.secs_left}s` : secsLeftLabel}</Table.Td>
                    <Table.Td>{t ? <Decimal value={t.btc_price} prefix="$" /> : "—"}</Table.Td>
                    <Table.Td>{t ? fmtPct(midOdds(t.up_bid, t.up_ask)) : "—"}</Table.Td>
                    <Table.Td>{t ? fmtPct(midOdds(t.down_bid, t.down_ask)) : "—"}</Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      )}
    </Card>
  );
}

function fmtPct(v: string | null): string {
  if (v == null) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `${(n * 100).toFixed(1)}%`;
}

/**
 * Bid/ask midpoint used by the trading-bot's `decide()` for all
 * min_odds / max_odds gating. The Condition check "Current" column
 * displays this same mid value (via `favorite_odds`), so the Price
 * feed table needs to use it too — otherwise the operator sees the
 * ask in one table and the mid in the other and they look mismatched.
 */
function midOdds(bid: string | null, ask: string | null): string | null {
  if (bid == null || ask == null) return null;
  const b = Number(bid);
  const a = Number(ask);
  if (!Number.isFinite(b) || !Number.isFinite(a)) return null;
  return ((b + a) / 2).toString();
}

// ----------------------------------------------------------- Condition Checks

type CheckStatus = "Checks not passed" | "Order Placed and Executed" | "Order Placed, Not Filled" | "Error";

function isSameSecond(a: string, b: string): boolean {
  const aMs = Date.parse(a);
  const bMs = Date.parse(b);
  if (!Number.isFinite(aMs) || !Number.isFinite(bMs)) return false;
  return Math.floor(aMs / 1000) === Math.floor(bMs / 1000);
}

function mapCheckStatus(
  row: DecisionCheckRow,
  betStatus: AnalyticsWindowDetail["bet_status"] | null,
  executionTs: string | null,
  noFillAttemptTimestamps: string[],
): CheckStatus {
  const reason = (row.block_reason ?? "").toLowerCase();
  const momentumFailed = momentumPassed(row) === false;
  if (noFillAttemptTimestamps.some((ts) => isSameSecond(row.check_ts, ts))) {
    return "Order Placed, Not Filled";
  }
  if (reason.includes("error")) return "Error";
  if (betStatus !== "no_fill" && executionTs != null && isSameSecond(row.check_ts, executionTs)) {
    return "Order Placed and Executed";
  }
  if (row.outcome === "already_placed") return "Order Placed and Executed";
  if (row.outcome === "place") {
    if (momentumFailed) return "Checks not passed";
    return "Order Placed, Not Filled";
  }
  if (row.outcome === "blocked" || row.outcome === "monitor" || row.outcome === "skip") {
    return "Checks not passed";
  }
  return "Error";
}

function trimChecksAfterExecution(
  checks: DecisionCheckRow[],
  betStatus: AnalyticsWindowDetail["bet_status"] | null,
  executionTs: string | null,
  noFillAttemptTimestamps: string[],
): DecisionCheckRow[] {
  if (checks.length === 0) return checks;
  const cutIdx = checks.findIndex(
    (row) => mapCheckStatus(row, betStatus, executionTs, noFillAttemptTimestamps) === "Order Placed and Executed",
  );
  if (cutIdx < 0) return checks;
  return checks.slice(0, cutIdx + 1);
}

function statusColor(status: CheckStatus): string {
  if (status === "Order Placed and Executed") return "green";
  if (status === "Order Placed, Not Filled") return "yellow";
  if (status === "Checks not passed") return "gray";
  return "red";
}

function checkField(row: DecisionCheckRow, name: string): { value?: unknown; detail?: unknown } | null {
  const items = Array.isArray(row.checks) ? row.checks : [];
  for (const raw of items) {
    if (!raw || typeof raw !== "object") continue;
    const obj = raw as Record<string, unknown>;
    if (obj.name === name) return { value: obj.value, detail: obj.detail };
  }
  return null;
}

function momentumLabel(row: DecisionCheckRow, priorRows: DecisionCheckRow[]): string {
  const own = checkField(row, "odds_momentum");
  const f = own ?? (() => {
    for (let i = priorRows.length - 1; i >= 0; i -= 1) {
      const prev = checkField(priorRows[i]!, "odds_momentum");
      if (prev) return prev;
    }
    return null;
  })();
  if (!f) return "—";
  // Canonical persisted shape (STRATEGY_SPEC.md §4): value is an object
  // with `observed` + `increasing` numbers and a `prior` array.
  if (f.value && typeof f.value === "object" && !Array.isArray(f.value)) {
    const v = f.value as { observed?: unknown; increasing?: unknown; avg_diff?: unknown; avg_passed?: unknown };
    if (typeof v.increasing === "number" && typeof v.observed === "number") {
      let label = `${v.increasing}/${v.observed}`;
      if (typeof v.avg_diff === "number" && v.avg_passed === false) {
        label += ` ·spike`;
      }
      return label;
    }
  }
  // Legacy string shape (older persisted rows or the fallback engine).
  if (typeof f.value === "string") {
    const m = f.value.match(/(\d+\s*\/\s*\d+)/);
    if (m?.[1]) return m[1].replace(/\s+/g, "");
  }
  return "—";
}

function uniqueOddsLabel(row: DecisionCheckRow): string {
  // Prefer the canonical `odds_momentum.value.prior` array (post-spec).
  const mom = checkField(row, "odds_momentum");
  if (mom && mom.value && typeof mom.value === "object" && !Array.isArray(mom.value)) {
    const v = mom.value as { prior?: unknown; current?: unknown };
    if (Array.isArray(v.prior)) {
      const items: string[] = [];
      for (const p of v.prior) {
        if (typeof p === "number" && Number.isFinite(p)) items.push(p.toFixed(3));
      }
      if (typeof v.current === "number" && Number.isFinite(v.current)) {
        items.push((v.current as number).toFixed(3));
      }
      if (items.length) return items.join(", ");
    }
  }
  // Legacy `unique_odds_checked` for older rows / fallback reconstruction.
  const f = checkField(row, "unique_odds_checked");
  if (!f) return "—";
  if (Array.isArray(f.value)) {
    const vals = f.value.filter((x): x is string => typeof x === "string");
    return vals.length ? vals.join(", ") : "—";
  }
  return "—";
}

function momentumEntry(row: DecisionCheckRow): Record<string, unknown> | null {
  const items = Array.isArray(row.checks) ? row.checks : [];
  for (const raw of items) {
    if (!raw || typeof raw !== "object") continue;
    const obj = raw as Record<string, unknown>;
    if (obj.name === "odds_momentum") return obj;
  }
  return null;
}

function asFiniteNum(value: unknown): number | null {
  const n = typeof value === "string" ? Number(value) : typeof value === "number" ? value : NaN;
  return Number.isFinite(n) ? n : null;
}

function avgMomentumPlaceholder(): JSX.Element {
  return (
    <Stack gap={2} style={{ whiteSpace: "nowrap" }}>
      <Text style={monoStyle} fw={600} c="dimmed">—</Text>
      <Text style={monoStyle} size="sm" c="dimmed">—</Text>
    </Stack>
  );
}

function avgMomentumCell(row: DecisionCheckRow, rawAvg: number | null = null): JSX.Element {
  const entry = momentumEntry(row);
  if (!entry) return avgMomentumPlaceholder();
  const value = entry.value && typeof entry.value === "object" && !Array.isArray(entry.value)
    ? (entry.value as Record<string, unknown>)
    : null;
  const threshold = entry.threshold && typeof entry.threshold === "object" && !Array.isArray(entry.threshold)
    ? (entry.threshold as Record<string, unknown>)
    : null;
  if (!value || !threshold) return avgMomentumPlaceholder();
  const avgLookback = asFiniteNum(threshold.avg_lookback);
  const avgMaxDiff = asFiniteNum(threshold.avg_max_diff);
  if (avgLookback == null || avgLookback <= 0 || avgMaxDiff == null || avgMaxDiff <= 0) {
    return avgMomentumPlaceholder();
  }
  const avgDiff = asFiniteNum(value.avg_diff);
  const avgPassed = typeof value.avg_passed === "boolean" ? value.avg_passed : null;
  const avgPriorRaw = Array.isArray(value.avg_prior) ? (value.avg_prior as unknown[]) : [];
  const avgPrior = avgPriorRaw
    .map((v) => asFiniteNum(v))
    .filter((n): n is number => n != null);
  const current = asFiniteNum(value.current) ?? asFiniteNum(row.favorite_odds);
  let avgVal: number | null = null;
  if (avgPrior.length > 0) {
    avgVal = avgPrior.reduce((s, v) => s + v, 0) / avgPrior.length;
  } else if (current != null && avgDiff != null) {
    avgVal = current - avgDiff;
  }
  if (avgVal == null || avgDiff == null) {
    return avgMomentumPlaceholder();
  }
  const diffLabel = `${avgDiff >= 0 ? "+" : "-"}${Math.abs(avgDiff).toFixed(3)}`;
  const diffColor = avgPassed === false
    ? "var(--mantine-color-red-7)"
    : avgPassed === true
      ? "var(--mantine-color-green-7)"
      : undefined;
  const sampleCount = avgPrior.length > 0 ? avgPrior.length : avgLookback;
  return (
    <Stack gap={2} style={{ whiteSpace: "nowrap" }}>
      <Group gap={6} wrap="nowrap" align="baseline">
        <Text style={monoStyle} fw={600}>{avgVal.toFixed(3)}</Text>
        <Text style={monoStyle} size="xs" c="dimmed">n={sampleCount}</Text>
      </Group>
      {rawAvg != null ? (
        <Text style={monoStyle} size="xs" c="yellow.5">raw49: {rawAvg.toFixed(3)}</Text>
      ) : null}
      <Group gap={4} wrap="nowrap">
        <Text style={{ ...monoStyle, color: diffColor }} size="sm">{diffLabel}</Text>
        <Text style={monoStyle} size="sm" c="dimmed">/ {avgMaxDiff.toFixed(3)}</Text>
        {passFailIcon(avgPassed)}
      </Group>
    </Stack>
  );
}

function momentumPassed(row: DecisionCheckRow): boolean | null {
  const items = Array.isArray(row.checks) ? row.checks : [];
  for (const raw of items) {
    if (!raw || typeof raw !== "object") continue;
    const obj = raw as Record<string, unknown>;
    if (obj.name !== "odds_momentum") continue;
    if (typeof obj.passed === "boolean") return obj.passed;
    return null;
  }
  return null;
}

function checkedOddsSummary(row: DecisionCheckRow, priorRows: DecisionCheckRow[]): { passed: boolean | null; values: string[] } {
  const own = uniqueOddsLabel(row);
  let passed = momentumPassed(row);
  if (passed == null && (row.outcome === "place" || row.outcome === "already_placed")) {
    passed = true;
  }
  if (own !== "—") return { passed, values: own.split(", ") };

  for (let i = priorRows.length - 1; i >= 0; i -= 1) {
    const prev = priorRows[i]!;
    const prevOdds = uniqueOddsLabel(prev);
    if (prevOdds === "—") continue;
    const prevPassed = momentumPassed(prev);
    return { passed: passed ?? prevPassed, values: prevOdds.split(", ") };
  }

  return { passed, values: [] };
}

function checkEntry(row: DecisionCheckRow, name: string): Record<string, unknown> | null {
  const items = Array.isArray(row.checks) ? row.checks : [];
  for (const raw of items) {
    if (!raw || typeof raw !== "object") continue;
    const obj = raw as Record<string, unknown>;
    if (obj.name === name) return obj;
  }
  return null;
}

function prevWindowsCell(row: DecisionCheckRow): JSX.Element {
  // The `prev_windows_direction` gate is only present once the decision
  // reaches it (odds + candles passed). Absent -> "—" (not evaluated).
  const entry = checkEntry(row, "prev_windows_direction");
  if (!entry) return <Text c="dimmed" size="md">—</Text>;
  const value = entry.value && typeof entry.value === "object" && !Array.isArray(entry.value)
    ? (entry.value as Record<string, unknown>) : null;
  const threshold = entry.threshold && typeof entry.threshold === "object" && !Array.isArray(entry.threshold)
    ? (entry.threshold as Record<string, unknown>) : null;
  const matches = value ? asFiniteNum(value.matches) : null;
  const count = threshold ? asFiniteNum(threshold.count) : null;
  const minMatches = threshold ? asFiniteNum(threshold.min_matches) : null;
  const label = matches != null && count != null
    ? `${Math.trunc(matches)}/${Math.trunc(count)}` : "—";
  const needLabel = minMatches != null ? `≥${Math.trunc(minMatches)}` : "";
  const passed = typeof entry.passed === "boolean" ? entry.passed : null;
  const detail = typeof entry.detail === "string" ? entry.detail : undefined;
  return (
    <Group gap={6} wrap="nowrap" title={detail}>
      <Text style={monoStyle}>{label}</Text>
      {needLabel ? <Text style={monoStyle} size="xs" c="dimmed">{needLabel}</Text> : null}
      {passFailIcon(passed)}
    </Group>
  );
}

function kronosCell(row: DecisionCheckRow): JSX.Element {
  // The `kronos_check` gate is only present once the decision reaches it
  // (all prior gates passed). Absent -> "—" (not evaluated).
  const entry = checkEntry(row, "kronos_check");
  if (!entry) return <Text c="dimmed" size="md">—</Text>;
  const value = entry.value && typeof entry.value === "object" && !Array.isArray(entry.value)
    ? (entry.value as Record<string, unknown>) : null;
  const threshold = entry.threshold && typeof entry.threshold === "object" && !Array.isArray(entry.threshold)
    ? (entry.threshold as Record<string, unknown>) : null;
  const minConf = threshold ? asFiniteNum(threshold.min_confidence) : null;
  const needLabel = minConf != null ? `≥${Math.round(minConf * 100)}%` : "";
  const passed = typeof entry.passed === "boolean" ? entry.passed : null;
  const detail = typeof entry.detail === "string" ? entry.detail : undefined;
  if (!value) {
    // Cold start: no cached forecast for this window (gate fails closed).
    return (
      <Group gap={6} wrap="nowrap" title={detail ?? "no cached Kronos forecast (cold start)"}>
        <Text style={monoStyle} c="dimmed">cold</Text>
        {passFailIcon(passed)}
      </Group>
    );
  }
  const predictedDir = typeof value.predicted_direction === "string" ? value.predicted_direction : "?";
  const probPicked = asFiniteNum(value.prob_picked);
  const probLabel = probPicked != null ? `${Math.round(probPicked * 100)}%` : "—";
  return (
    <Group gap={6} wrap="nowrap" title={detail}>
      <Text style={monoStyle}>{predictedDir} {probLabel}</Text>
      {needLabel ? <Text style={monoStyle} size="xs" c="dimmed">{needLabel}</Text> : null}
      {passFailIcon(passed)}
    </Group>
  );
}

function candlesLabel(row: DecisionCheckRow, priorRows: DecisionCheckRow[]): string {
  if (row.matching_candles != null && row.candles_evaluated != null) {
    return `${row.matching_candles} / ${row.candles_evaluated}`;
  }
  for (let i = priorRows.length - 1; i >= 0; i -= 1) {
    const prev = priorRows[i]!;
    if (prev.matching_candles != null && prev.candles_evaluated != null) {
      return `${prev.matching_candles} / ${prev.candles_evaluated}`;
    }
  }
  return "— / —";
}

function oddsChips(values: string[]): Array<{ value: string; passed: boolean | null }> {
  if (values.length === 0) return [];
  const nums = values.map((v) => Number(v));
  if (nums.some((n) => !Number.isFinite(n))) {
    return values.map((value) => ({ value, passed: null }));
  }
  const current = nums[nums.length - 1]!;
  return values.map((value, i) => {
    if (i === values.length - 1) return { value, passed: true };
    return { value, passed: nums[i]! < current };
  });
}

function ConditionCheckSection({ checks, ticks, windowStart, windowSeconds, loading, synthetic, betStatus, executionTs, noFillAttemptTimestamps, minOdds, maxOdds, minMatchingCandles, momentumEnabled, prevWindowsEnabled, kronosEnabled, slice, simulateEnabled, onToggleSimulate }:
  {
    checks: DecisionCheckRow[];
    ticks: WindowTick[];
    windowStart: string;
    windowSeconds: number;
    loading: boolean;
    synthetic?: boolean;
    betStatus: AnalyticsWindowDetail["bet_status"] | null;
    executionTs: string | null;
    noFillAttemptTimestamps: string[];
    minOdds: number;
    maxOdds: number;
    minMatchingCandles: number;
    // Gate columns are rendered only when the gate is enabled in the
    // config governing THIS window (mirrors the Live page behaviour).
    momentumEnabled: boolean;
    prevWindowsEnabled: boolean;
    kronosEnabled: boolean;
    /**
     * V3 multi-market: hierarchical slice picker state shared with
     * the Price-feed section. When complete, ``computeSliceRange``
     * returns a [start, length] window in seconds that we use to
     * narrow ``checksBySecond`` to just that slice's rows so the two
     * tables stay in lockstep.
     */
    slice: SliceState;
    /**
     * Whether the operator has clicked "Simulate checks". While this
     * is off we render only the persisted decision_checks rows
     * (typically a single ORDER PLACED row). While on, the parent
     * synthesises the full per-tick feed from the recorded ticks +
     * window config and passes it through here.
     */
    simulateEnabled: boolean;
    onToggleSimulate: () => void;
  }) {
  const [rawLookback, setRawLookback] = useState<number | string>(49);
  const rawLookbackNum = typeof rawLookback === "number" && rawLookback >= 1 && rawLookback <= 300
    ? Math.trunc(rawLookback) : 49;
  const allChecksBySecond = useMemo(
    () => collapseChecksBySecond(checks, windowStart, windowSeconds),
    [checks, windowStart, windowSeconds],
  );
  // V3 multi-market: filter the collapsed rows to the active slice from
  // the price-feed pills. ``range == null`` means the user hasn't
  // finished picking yet (typically only the first render before the
  // ticks-driven auto-init runs); fall back to showing every row so
  // the table is never mysteriously blank.
  const sliceRange = useMemo(() => computeSliceRange(windowSeconds, slice), [windowSeconds, slice]);
  // V3 multi-market: keep the full row's index into ``allChecksBySecond``
  // alongside each filtered row so the odds-history / candles /
  // momentum lookups (which walk all preceding checks) stay correct
  // when the slice only exposes a 60s window. Without this, switching
  // to a later slice would make every row look like the first check
  // ever observed and chips / candles columns would jump.
  const scopedChecks = useMemo<Array<{ row: DecisionCheckRow; fullIdx: number }>>(() => {
    if (!sliceRange) {
      return allChecksBySecond.map((row, i) => ({ row, fullIdx: i }));
    }
    const windowStartMs = Date.parse(windowStart);
    if (!Number.isFinite(windowStartMs)) {
      return allChecksBySecond.map((row, i) => ({ row, fullIdx: i }));
    }
    const fromMs = windowStartMs + sliceRange.offsetSec * 1000;
    const toMs = fromMs + sliceRange.lengthSec * 1000;
    const out: Array<{ row: DecisionCheckRow; fullIdx: number }> = [];
    for (let i = 0; i < allChecksBySecond.length; i += 1) {
      const row = allChecksBySecond[i]!;
      const ms = Date.parse(row.check_ts);
      if (Number.isFinite(ms) && ms >= fromMs && ms < toMs) out.push({ row, fullIdx: i });
    }
    return out;
  }, [allChecksBySecond, sliceRange, windowStart]);
  // ``rawAvgByCheckTs`` below still iterates over the visible rows
  // only -- it computes a per-row tick average so doesn't need
  // history beyond ``check_ts``. Provide ``checksBySecond`` as a
  // convenience alias of just the rows for that loop.
  const checksBySecond = useMemo(() => scopedChecks.map((s) => s.row), [scopedChecks]);
  const rawAvgByCheckTs = useMemo(() => {
    const map = new Map<string, number | null>();
    if (ticks.length === 0) return map;
    // Perf: pre-parse tick timestamps + mid values once so we don't burn
    // millions of Date.parse calls on 1h/4h windows.
    const parsed = ticks
      .map((t) => {
        const upMid = t.up_bid != null && t.up_ask != null
          ? (Number(t.up_bid) + Number(t.up_ask)) / 2 : null;
        const dnMid = t.down_bid != null && t.down_ask != null
          ? (Number(t.down_bid) + Number(t.down_ask)) / 2 : null;
        return { ms: Date.parse(t.ts), upMid, dnMid };
      })
      .sort((a, b) => a.ms - b.ms);
    for (const c of checksBySecond) {
      const dir = (c.direction ?? "UP").toUpperCase();
      const cutMs = Date.parse(c.check_ts);
      const mids: number[] = [];
      for (const p of parsed) {
        if (p.ms > cutMs) break;
        const chosen = dir === "UP"
          ? (p.upMid ?? (p.dnMid != null ? 1 - p.dnMid : null))
          : (p.dnMid ?? (p.upMid != null ? 1 - p.upMid : null));
        if (chosen != null && Number.isFinite(chosen)) mids.push(chosen);
      }
      const last49 = mids.slice(-rawLookbackNum);
      map.set(c.check_ts, last49.length > 0
        ? last49.reduce((s, v) => s + v, 0) / last49.length
        : null);
    }
    return map;
  }, [checksBySecond, ticks, rawLookbackNum]);

  // 7 always-present columns (Time, Check Status, Dir, Min, Current, Max,
  // Candles) plus whichever gate columns are enabled. Used for the
  // full-width "Odds checked" row's colSpan so it never mismatches.
  const condCheckTotalCols =
    7 + (momentumEnabled ? 2 : 0) + (prevWindowsEnabled ? 1 : 0) + (kronosEnabled ? 1 : 0);

  return (
    <Card withBorder padding="md">
      <Group justify="space-between" align="center" mb="xs">
        <Title order={4}>Condition check (per tick)</Title>
        <Group gap="xs" align="center">
          <Button
            size="xs"
            variant={simulateEnabled ? "filled" : "light"}
            color={simulateEnabled ? "orange" : "blue"}
            onClick={onToggleSimulate}
          >
            {simulateEnabled ? "Hide simulated checks" : "Simulate checks for selected slice"}
          </Button>
          <Text size="sm" c="dimmed">Raw avg lookback:</Text>
          <NumberInput
            value={rawLookback}
            onChange={setRawLookback}
            min={1}
            max={300}
            step={1}
            w={80}
            size="xs"
            hideControls={false}
          />
        </Group>
      </Group>
      {synthetic ? (
        <Text size="md" c="dimmed" mb="xs">
          Per-tick feed synthesized from recorded ticks + the window's stored config.
          Persisted rows written by the live bot take precedence on their exact second.
        </Text>
      ) : null}
      {loading ? <LoadingState label="loading checks…" /> : checks.length === 0 ? (
        <Text c="dimmed" size="md">
          {simulateEnabled
            ? "No decision checks to display (missing config or no ticks for this window)."
            : "Only the order-placement check is persisted for live windows. Click \"Simulate checks for selected slice\" to compute the full per-tick feed."}
        </Text>
      ) : scopedChecks.length === 0 ? (
        // V3 multi-market: window has checks but the active price-feed
        // slice picker excludes them all (e.g. operator drilled into a
        // 1-min slot that came after the bet was executed). Tell the
        // operator instead of rendering an empty grid.
        <Text c="dimmed" size="md">No decision checks in the selected slice.</Text>
      ) : (
        <ScrollArea h={810}>
          <Table
            withTableBorder
            horizontalSpacing="sm"
            verticalSpacing={7}
            style={monoStyle}
            fz="md"
            stickyHeader
            stickyHeaderOffset={0}
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Time</Table.Th>
                <Table.Th>Check Status</Table.Th>
                <Table.Th>Dir</Table.Th>
                <Table.Th>Min</Table.Th>
                <Table.Th>Current</Table.Th>
                <Table.Th>Max</Table.Th>
                <Table.Th>Candles</Table.Th>
                {momentumEnabled ? <Table.Th>Momentum</Table.Th> : null}
                {momentumEnabled ? <Table.Th>Avg Momentum</Table.Th> : null}
                {prevWindowsEnabled ? <Table.Th>Prev Windows</Table.Th> : null}
                {kronosEnabled ? <Table.Th>Kronos</Table.Th> : null}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {scopedChecks.map(({ row: c, fullIdx }) => {
                // History-aware lookups (odds chips, candles label,
                // momentum label) walk every preceding check across
                // the FULL window via ``fullIdx``, so chips reflect
                // true trend even when the slice only renders a 60s
                // sub-range.
                const odds = checkedOddsSummary(c, allChecksBySecond.slice(0, fullIdx));
                const chips = oddsChips(odds.values);
                const status = mapCheckStatus(c, betStatus, executionTs, noFillAttemptTimestamps);
                const timeLabel = checkTimeLabel(c.check_ts, windowStart, windowSeconds);
                const fav = c.favorite_odds == null ? null : Number(c.favorite_odds);
                const favFinite = fav != null && Number.isFinite(fav);
                const minPassed = favFinite ? fav >= minOdds : null;
                const maxPassed = favFinite ? fav <= maxOdds : null;
                const matching = c.matching_candles;
                const candlesPassed = matching == null ? null : matching >= minMatchingCandles;
                return (
                  <Fragment key={`${c.check_ts}-${fullIdx}`}>
                    <Table.Tr key={`${c.check_ts}-${fullIdx}`}>
                      <Table.Td>{timeLabel}</Table.Td>
                      <Table.Td>
                        <Badge
                          size="md"
                          variant="filled"
                          styles={{ root: pillStylesForTone(statusColor(status)) }}
                        >
                          {status}
                        </Badge>
                      </Table.Td>
                      <Table.Td>{c.direction ?? "—"}</Table.Td>
                      <Table.Td>
                        <Group gap={6} wrap="nowrap">
                          <Text style={monoStyle}>{formatPctNum(minOdds)}</Text>
                          {passFailIcon(minPassed)}
                        </Group>
                      </Table.Td>
                      <Table.Td style={monoStyle}>{favFinite ? formatPctNum(fav) : "—"}</Table.Td>
                      <Table.Td>
                        <Group gap={6} wrap="nowrap">
                          <Text style={monoStyle}>{formatPctNum(maxOdds)}</Text>
                          {passFailIcon(maxPassed)}
                        </Group>
                      </Table.Td>
                      <Table.Td style={{ whiteSpace: "nowrap" }}>
                        <Group gap={6} wrap="nowrap">
                          <Text style={monoStyle}>{candlesLabel(c, checks.slice(0, fullIdx))}</Text>
                          {passFailIcon(candlesPassed)}
                        </Group>
                      </Table.Td>
                      {momentumEnabled ? (
                        <Table.Td style={{ whiteSpace: "nowrap" }}>
                          <Group gap={6} wrap="nowrap">
                            <Text style={monoStyle}>{momentumLabel(c, allChecksBySecond.slice(0, fullIdx))}</Text>
                            {passFailIcon(momentumPassed(c))}
                          </Group>
                        </Table.Td>
                      ) : null}
                      {momentumEnabled ? (
                        <Table.Td>{avgMomentumCell(c, rawAvgByCheckTs.get(c.check_ts) ?? null)}</Table.Td>
                      ) : null}
                      {prevWindowsEnabled ? (
                        <Table.Td style={{ whiteSpace: "nowrap" }}>{prevWindowsCell(c)}</Table.Td>
                      ) : null}
                      {kronosEnabled ? (
                        <Table.Td style={{ whiteSpace: "nowrap" }}>{kronosCell(c)}</Table.Td>
                      ) : null}
                    </Table.Tr>
                    <Table.Tr key={`${c.check_ts}-${fullIdx}-odds`}>
                      <Table.Td colSpan={condCheckTotalCols}>
                        <Group gap={6} wrap="wrap">
                          <Text size="md" c="dimmed">Odds checked:</Text>
                          {chips.length === 0 ? (
                            <Badge
                              size="lg"
                              variant="filled"
                              styles={{ root: pillStylesForTone(odds.passed === false ? "red" : odds.passed === true ? "green" : "gray") }}
                            >—</Badge>
                          ) : chips.map((o, i) => (
                            <Badge
                              key={`${c.check_ts}-${fullIdx}-odd-${i}`}
                              size="lg"
                              variant="filled"
                              styles={{ root: pillStylesForTone(o.passed === false ? "red" : o.passed === true ? "green" : "gray") }}
                            >
                              {o.value}
                            </Badge>
                          ))}
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  </Fragment>
                );
              })}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      )}
    </Card>
  );
}

void Box;

// ---- helpers used by ConditionCheckSection -------------------------------

function formatPctNum(value: unknown): string {
  const n = typeof value === "string" ? Number(value) : typeof value === "number" ? value : NaN;
  return Number.isFinite(n) ? `${Math.round((n as number) * 100)}%` : "—";
}

function passFailIcon(passed: boolean | null): JSX.Element {
  if (passed == null) return <Text c="dimmed" size="md">—</Text>;
  if (passed) return <IconCheck size={20} color="var(--mantine-color-green-6)" />;
  return <IconX size={20} color="var(--mantine-color-red-6)" />;
}

function pillStylesForTone(tone: "green" | "red" | "yellow" | "gray" | string): React.CSSProperties {
  const t = String(tone || "gray").toLowerCase();
  let bg = "var(--mantine-color-gray-8)";
  if (t === "green") bg = "var(--mantine-color-green-8)";
  else if (t === "red") bg = "var(--mantine-color-red-8)";
  else if (t === "yellow") bg = "var(--mantine-color-yellow-9)";
  return {
    backgroundColor: bg,
    color: "var(--mantine-color-white)",
    fontWeight: 800,
    fontSize: "0.92rem",
    lineHeight: 1.2,
    padding: "7px 12px",
    minWidth: 56,
    textAlign: "center",
    letterSpacing: 0.3,
    textShadow: "0 1px 1px rgba(0,0,0,0.35)",
  };
}

/**
 * The bot's persisted checks don't include `odds_momentum` or
 * `unique_odds_checked`, so the historical odds pill row would otherwise
 * be empty on real (non-fallback) windows. Reconstruct that data from
 * the per-second ticks so the pills always render.
 */
function enrichChecksWithMomentum(
  rows: DecisionCheckRow[],
  ticks: WindowTick[],
  cfg: ReturnType<typeof resolveDecisionConfig>,
): DecisionCheckRow[] {
  if (rows.length === 0 || ticks.length === 0) return rows;

  // Perf: the previous implementation was O(N * T) — for every check row
  // it re-parsed and re-scanned the whole tick stream, freezing the
  // Market Data page on 1h/4h windows (14400 ticks × 14400 rows ≈ 2×10^8
  // Date.parse calls). We now walk ticks and rows once in time order,
  // maintaining incremental per-direction histories that `momentumFromState`
  // consumes directly.
  const sortedTicks = [...ticks].sort(
    (a, b) => Date.parse(a.ts) - Date.parse(b.ts),
  );
  const tickMs: number[] = new Array(sortedTicks.length);
  const upMidPerTick: Array<number | null> = new Array(sortedTicks.length);
  const dnMidPerTick: Array<number | null> = new Array(sortedTicks.length);
  for (let i = 0; i < sortedTicks.length; i += 1) {
    const t = sortedTicks[i]!;
    tickMs[i] = Date.parse(t.ts);
    upMidPerTick[i] = sideOdds(t, "UP");
    dnMidPerTick[i] = sideOdds(t, "DOWN");
  }

  const useUnique = cfg.odds_momentum_use_unique_odds;
  const historyUp: number[] = [];
  const historyDn: number[] = [];
  const seenUp: Set<number> | null = useUnique ? new Set() : null;
  const seenDn: Set<number> | null = useUnique ? new Set() : null;
  let lastMidUp: number | null = null;
  let lastMidDn: number | null = null;
  let lastSecsLeft = 0;

  // Sort rows by check_ts (ascending) while remembering the original index
  // so we can restore the output order at the end.
  const rowsIndexed = rows.map((r, idx) => ({ r, idx, ms: Date.parse(r.check_ts) }));
  rowsIndexed.sort((a, b) => a.ms - b.ms);

  const output: DecisionCheckRow[] = new Array(rows.length);
  let tickIdx = 0;
  for (const { r: row, idx, ms: cutoff } of rowsIndexed) {
    // Advance the tick cursor up to and including the row's check_ts,
    // appending to the incremental histories in the process.
    while (tickIdx < sortedTicks.length && tickMs[tickIdx]! <= cutoff) {
      const upM = upMidPerTick[tickIdx];
      const dnM = dnMidPerTick[tickIdx];
      if (upM != null && Number.isFinite(upM)) {
        lastMidUp = upM;
        if (seenUp) {
          if (!seenUp.has(upM)) { seenUp.add(upM); historyUp.push(upM); }
        } else {
          historyUp.push(upM);
        }
      }
      if (dnM != null && Number.isFinite(dnM)) {
        lastMidDn = dnM;
        if (seenDn) {
          if (!seenDn.has(dnM)) { seenDn.add(dnM); historyDn.push(dnM); }
        } else {
          historyDn.push(dnM);
        }
      }
      lastSecsLeft = sortedTicks[tickIdx]!.secs_left;
      tickIdx += 1;
    }

    const existing: unknown[] = Array.isArray(row.checks) ? row.checks : [];
    const named = (n: string) =>
      existing.some((c) => typeof c === "object" && c !== null && (c as { name?: unknown }).name === n);
    const hasMomentum = named("odds_momentum");
    const hasUnique = named("unique_odds_checked");
    if (hasMomentum || !row.direction || tickIdx === 0) {
      output[idx] = row;
      continue;
    }
    const history = row.direction === "UP" ? historyUp : historyDn;
    const lastMid = row.direction === "UP" ? lastMidUp : lastMidDn;
    const snap = momentumFromState(history, lastMid, lastSecsLeft, cfg);
    const additions: unknown[] = [];
    if (!hasMomentum) {
      additions.push({
        name: "odds_momentum",
        passed: snap.bypassed ? true : snap.passed,
        value: {
          observed: snap.observed,
          increasing: snap.increasing,
          current: snap.current,
          prior: snap.prior,
          avg_diff: snap.avgDiff,
          avg_prior: snap.avgPrior,
          avg_passed: snap.avgPassed,
        },
        threshold: {
          lookback: snap.lookback,
          required_increasing: snap.required,
          avg_lookback: snap.avgLookback,
          avg_max_diff: snap.avgMaxDiff,
        },
        detail: snap.detail,
      });
    }
    if (!hasUnique) {
      additions.push({
        name: "unique_odds_checked",
        passed: null,
        value: snap.checkedOdds,
        threshold: cfg.odds_momentum_lookback + 1,
        detail: `${snap.checkedOdds.length}/${cfg.odds_momentum_lookback + 1} recent ${cfg.odds_momentum_use_unique_odds ? "unique" : "recorded"} odds`,
      });
    }
    output[idx] = { ...row, checks: [...existing, ...additions] };
  }
  return output;
}
